import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Phone, Mail, MapPin, Briefcase, Building2, CalendarClock,
  User, Send, PhoneCall, ThumbsUp, HelpCircle, XCircle, Clock, FileText, Pencil, Check, X,
  Trash2, ArrowRightLeft, Workflow, Info, FileX2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StatusCell } from "@/components/StatusCell";
import { RiskBadge } from "@/components/RiskBadge";
import WorkerDocuments from "@/components/WorkerDocuments";
import ReassignDialog from "@/components/ReassignDialog";
import MandanteLogo from "@/components/MandanteLogo";
import { INTENT_CONFIG, formatDate, relativeDays, CHANNEL_LABEL } from "@/lib/format";
import { formatRut, isValidRut } from "@/lib/rut";
import { normalizeClPhone, isValidClPhone, CL_PREFIX, normalizeClWhatsapp, isValidClWhatsapp, WA_PREFIX } from "@/lib/phone";

const WORKER_FIELDS = ["rut", "first_name", "last_name", "email", "phone", "whatsapp", "city",
  "position", "company", "mandante", "contrato", "project", "assigned_user",
  "expected_hire_date", "due_date", "whatsapp_opt_in", "visa_temporaria"];

const ACTION_LABEL = {
  CREATE: "Creación", UPDATE: "Actualización", DELETE: "Eliminación",
  DOCUMENT_UPLOAD: "Subida documento", DOCUMENT_REPLACE: "Reemplazo documento",
  DOCUMENT_DELETE: "Eliminación documento",
  STATUS_CHANGE: "Cambio estado", REASSIGN: "Reasignación",
  BULK_REMINDER: "Recordatorio", MANUAL_CONTACT: "Contacto manual",
  SCAN_CREDENCIAL: "Escaneo credencial",
};

export default function AcreditacionTrabajadorDetail() {
  const { id: mid, cid, wid } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canEdit = ["ADMIN", "HR_MANAGER", "RECRUITER", "SUPERVISOR"].includes(user?.role);
  const canSeeAudit = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  const [mandante, setMandante] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [mandantes, setMandantes] = useState([]);
  const [w, setW] = useState(null);
  const [jornadas, setJornadas] = useState([]);
  const [workflow, setWorkflow] = useState([]);
  const [docSummary, setDocSummary] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTab, setReassignTab] = useState("move");
  const [deleting, setDeleting] = useState(false);
  const [showDatos, setShowDatos] = useState(false);

  const load = async () => {
    try {
      const wk = await api.get(`/workers/${wid}`).then((r) => r.data);
      const jo = await api.get(`/workers/${wid}/jornadas`).then((r) => r.data).catch(() => []);
      const ms = await api.get("/mandantes").then((r) => r.data);
      // Si el worker quedó sin asignación (finiquito / traslado) pero la URL
      // todavía apunta a un contrato, redirigimos a la ficha general.
      if (mid && cid && !wk.mandante && !wk.contrato && location.pathname.includes("/acreditacion/")) {
        nav(`/trabajadores/${wid}/ficha`, { replace: true });
        return;
      }
      // Si la ruta no trae mid/cid (ficha general), resolver desde el worker.
      let effMid = mid;
      let effCid = cid;
      if ((!effMid || !effCid) && wk.mandante) {
        const m = ms.find((x) => x.name === wk.mandante || x.name === wk.company);
        if (m) {
          effMid = m.id;
          const c = (m.contratos || []).find((x) => x.name === wk.contrato);
          if (c) effCid = c.id;
        }
      }
      let m = null;
      let c = null;
      if (effMid) {
        m = ms.find((x) => x.id === effMid) || null;
        if (m && effCid) {
          c = await api.get(`/mandantes/${effMid}/contratos/${effCid}`).then((r) => r.data).catch(() => null);
        }
      }
      // Si el worker fue finiquitado/trasladado no mostramos mandante/contrato aunque sigan en la URL
      if (!wk.mandante && !wk.contrato) { m = null; c = null; }
      setMandantes(ms); setMandante(m); setContrato(c); setW(wk); setJornadas(jo);
      if (effMid && wk.mandante) {
        try { const wf = await api.get(`/mandantes/${effMid}/workflow`).then((r) => r.data); setWorkflow(wf || []); } catch { setWorkflow([]); }
      } else { setWorkflow([]); }
      if (canSeeAudit) {
        try { const al = await api.get("/audit-logs", { params: { worker_id: wid } }).then((r) => r.data); setAuditRows(al || []); }
        catch { setAuditRows([]); }
      }
    } catch (e) {
      toast.error("No se pudo cargar");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mid, cid, wid]);

  // Merge timeline + audit rows into a unified sorted list (newest first)
  // IMPORTANT: hooks must be called before any early return
  const historial = useMemo(() => {
    if (!w) return [];
    const fromAudit = auditRows.map((r) => ({
      at: r.created_at, source: "audit",
      user_name: r.user_name || r.user_email || "—",
      action: ACTION_LABEL[r.action] || r.action,
      action_code: r.action,
      detail: buildAuditDetail(r),
    }));
    const fromTl = (w.timeline || []).map((t) => ({
      at: t.at, source: "timeline",
      user_name: t.user_name || t.user_email || "Sistema",
      action: tlActionLabel(t),
      action_code: (t.type || "").toUpperCase(),
      detail: buildTimelineDetail(t),
      _doc_name: t.document_name || "",
    }));
    // Document events are logged in BOTH audit and timeline with slightly
    // different timestamps. Audit rows are richer (filename, expiration, user),
    // so when the user has audit visibility (ADMIN/HR_MANAGER) we drop ALL
    // timeline doc entries and keep only the audit ones.
    const hasDocAudit = auditRows.some((r) => (r.action || "").startsWith("DOCUMENT_"));
    const merged = [...fromAudit, ...fromTl.filter((t) => {
      if (hasDocAudit && t.source === "timeline" && t.action_code === "DOCUMENT") return false;
      return true;
    })];
    merged.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    return merged;
  }, [auditRows, w]);

  if (!w) return <div className="text-sm text-slate-500" data-testid="worker-detail-loading">Cargando…</div>;
  const workerUnassigned = !(w.mandante || w.company) && !w.contrato;
  const isUnassigned = workerUnassigned || !mandante || !contrato;

  const contratosFor = (mandanteName) => (mandantes.find((m) => m.name === mandanteName)?.contratos) || [];

  const updateStatus = async (code, status) => {
    await api.patch(`/workers/${wid}/requirements/${code}`, { status });
    toast.success("Estado actualizado"); load();
  };
  const manualContact = async (chan) => {
    await api.post(`/workers/${wid}/manual-contact`, { channel: chan, note: "" });
    toast.success("Contacto registrado"); load();
  };
  const simulateResponse = async (resp) => {
    await api.post(`/workers/${wid}/response`, { response: resp });
    toast.success("Respuesta registrada"); load();
  };

  const saveFields = async (patch) => {
    const payload = {};
    WORKER_FIELDS.forEach((k) => (payload[k] = w[k] ?? ""));
    payload.whatsapp_opt_in = w.whatsapp_opt_in ?? true;
    payload.visa_temporaria = w.visa_temporaria ?? false;
    Object.assign(payload, patch);
    try { await api.put(`/workers/${wid}`, payload); toast.success("Datos actualizados"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); throw e; }
  };
  const saveField = (field, value) => saveFields({ [field]: value });

  const deleteWorker = async () => {
    setDeleting(true);
    try {
      await api.delete(`/workers/${wid}`);
      toast.success("Trabajador eliminado");
      nav(mid && cid ? `/acreditacion/${mid}/contratos/${cid}/trabajadores` : "/trabajadores");
    } catch { toast.error("No se pudo eliminar"); }
    finally { setDeleting(false); setConfirmDel(false); }
  };

  const intent = INTENT_CONFIG[w.intent_status] || INTENT_CONFIG.NONE;
  const currentStageId = w.workflow_stage_id || null;
  const stageIndex = workflow.findIndex((s) => s.id === currentStageId);

  return (
    <div className="space-y-6" data-testid="worker-detail-page">
      <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <Link to="/acreditacion" className="hover:text-emerald-600 inline-flex items-center gap-1"><ChevronLeft size={14} /> Acreditación</Link>
        {!isUnassigned && mandante && (
          <>
            <ChevronRight size={14} />
            <Link to={`/acreditacion/${mandante.id}`} className="hover:text-emerald-600">{mandante.name}</Link>
          </>
        )}
        {!isUnassigned && contrato && mandante && (
          <>
            <ChevronRight size={14} />
            <Link to={`/acreditacion/${mandante.id}/contratos/${contrato.id}`} className="hover:text-emerald-600">{contrato.name}</Link>
            <ChevronRight size={14} />
            <Link to={`/acreditacion/${mandante.id}/contratos/${contrato.id}/trabajadores`} className="hover:text-emerald-600">Trabajadores</Link>
          </>
        )}
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium truncate">{w.first_name} {w.last_name}</span>
      </nav>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start gap-4">
          {mandante && <MandanteLogo mandante={mandante} size="md" />}
          <Avatar className="h-14 w-14"><AvatarFallback className="bg-blue-600 text-white font-semibold">{(w.first_name?.[0] || "") + (w.last_name?.[0] || "")}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <h1 data-testid="worker-detail-name" className="font-display text-2xl font-bold text-slate-900 truncate">{w.first_name} {w.last_name}</h1>
            <p className="text-sm text-slate-500 truncate">{[w.position, isUnassigned ? null : (contrato?.name), isUnassigned ? null : (mandante?.name)].filter(Boolean).join(" · ") || "—"}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <RiskBadge level={w.risk_level} />
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${intent.cls}`}>{intent.label}</span>
              {w.rut && <span className="text-xs font-mono text-slate-500">RUT {w.rut}</span>}
              {isUnassigned && (
                <span data-testid="worker-detail-sin-asignacion"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                  SIN ASIGNACIÓN
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-4xl font-display font-bold text-blue-600 leading-none">{w.progress_percentage || 0}%</p>
            <p className="text-xs text-slate-400 mt-0.5">avance</p>
          </div>
          <Link to={!isUnassigned && mandante && contrato ? `/acreditacion/${mandante.id}/contratos/${contrato.id}/trabajadores` : "/trabajadores"}
            data-testid="worker-detail-back"
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg h-9">
            <ChevronLeft size={14} /> Volver
          </Link>
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button data-testid="worker-detail-open-datos" onClick={() => setShowDatos(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100">
            <Info size={13} /> Ver datos
          </button>
          {canEdit && (
            <>
              <button data-testid="worker-detail-reassign" onClick={() => { setReassignTab("move"); setReassignOpen(true); }}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100">
                <ArrowRightLeft size={13} /> Reasignar
              </button>
              <button data-testid="worker-detail-finiquitar" onClick={() => { setReassignTab("finiquito"); setReassignOpen(true); }}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-100">
                <FileX2 size={13} /> Finiquitar
              </button>
              <button data-testid="worker-detail-delete" onClick={() => setConfirmDel(true)}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-100">
                <Trash2 size={13} /> Eliminar
              </button>
            </>
          )}
        </div>
      </section>

      <Tabs defaultValue="documentos" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="documentos" data-testid="worker-tab-documentos">Documentos</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="worker-tab-timeline"><Workflow size={13} className="mr-1" /> Timeline</TabsTrigger>
            <TabsTrigger value="historial" data-testid="worker-tab-historial">Historial</TabsTrigger>
          </TabsList>
          {docSummary && (
            <div data-testid="worker-folder-progress-compact" className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-2 shadow-sm min-w-[260px]">
              <span className="text-xs font-medium text-slate-600">Carpeta</span>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${docSummary.progress >= 100 ? "bg-emerald-500" : docSummary.progress >= 50 ? "bg-blue-500" : "bg-amber-500"}`}
                  style={{ width: `${docSummary.progress}%` }} />
              </div>
              <span className="text-xs font-display font-bold text-slate-800 whitespace-nowrap">{docSummary.uploaded}/{docSummary.total} · {docSummary.progress}%</span>
            </div>
          )}
        </div>

        <TabsContent value="documentos">
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <WorkerDocuments worker={w} canEdit={canEdit} onChanged={load} onSummary={setDocSummary} hideFolderCard={true} />
          </section>
        </TabsContent>

        <TabsContent value="timeline">
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Workflow size={16} className="text-indigo-600" /> Timeline de acreditación
            </h3>
            {workflow.length === 0 ? (
              <p className="text-sm text-slate-400 italic">Este mandante aún no tiene un flujo configurado.</p>
            ) : (
              <ol className="space-y-3">
                {workflow.map((s, i) => {
                  const done = stageIndex > -1 && i < stageIndex;
                  const active = s.id === currentStageId;
                  const dotCls = active ? "bg-indigo-600 text-white ring-4 ring-indigo-100"
                              : done ? "bg-emerald-500 text-white"
                              : "bg-slate-200 text-slate-500";
                  const boxCls = active ? "border-indigo-300 bg-indigo-50/40"
                              : done ? "border-emerald-200 bg-emerald-50/30"
                              : "border-slate-200 bg-white";
                  return (
                    <li key={s.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${dotCls}`}>{i + 1}</div>
                        {i < workflow.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1 min-h-[16px]" />}
                      </div>
                      <div className={`flex-1 rounded-lg border p-3 ${boxCls}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                          {active && <span className="text-[10px] font-bold uppercase text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">Etapa actual</span>}
                          {done && <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Completada</span>}
                        </div>
                        {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </TabsContent>

        <TabsContent value="historial" className="space-y-6">
          {canEdit && (
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-display font-semibold text-slate-800 mb-2">Registrar contacto manual</h3>
              <div className="flex gap-2 flex-wrap">
                <ActionBtn onClick={() => manualContact("call")} icon={PhoneCall} label="Llamada" testid="manual-contact-call" />
                <ActionBtn onClick={() => manualContact("whatsapp")} icon={Send} label="WhatsApp" testid="manual-contact-whatsapp" />
                <ActionBtn onClick={() => manualContact("email")} icon={Mail} label="Email" testid="manual-contact-email" />
              </div>
              <h3 className="font-display font-semibold text-slate-800 mb-2 mt-5">Simular respuesta del trabajador</h3>
              <div className="flex gap-2 flex-wrap">
                <ActionBtn onClick={() => simulateResponse("CONTINUE")} icon={ThumbsUp} label="Continuar" testid="sim-continue" />
                <ActionBtn onClick={() => simulateResponse("NEEDS_HELP")} icon={HelpCircle} label="Ayuda" testid="sim-help" />
                <ActionBtn onClick={() => simulateResponse("WITHDRAW")} icon={XCircle} label="No sigue" testid="sim-withdraw" />
              </div>
            </section>
          )}

          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-800">Historial de actividad</h3>
              <span className="text-xs text-slate-400">{historial.length} evento(s)</span>
            </div>
            {historial.length === 0 ? (
              <p className="p-10 text-center text-sm text-slate-400 italic">Sin actividad registrada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="worker-historial-table">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                      <th className="px-4 py-2 whitespace-nowrap">Fecha</th>
                      <th className="px-4 py-2">Usuario</th>
                      <th className="px-4 py-2">Acción</th>
                      <th className="px-4 py-2">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((h, i) => (
                      <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">{fmtDateTime(h.at)}</td>
                        <td className="px-4 py-2 text-slate-700">{h.user_name}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeFor(h.action_code)}`}>{h.action}</span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">{h.detail || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </TabsContent>
      </Tabs>

      {/* Datos modal */}
      <Dialog open={showDatos} onOpenChange={setShowDatos}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Datos del trabajador</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  <InfoRow icon={User} label="RUT" value={w.rut} canEdit={canEdit} kind="rut" onSave={(v) => saveField("rut", v)} />
                  <InfoRow icon={Briefcase} label="Cargo" value={w.position} />
                  <AssignmentRow canEdit={canEdit} worker={w} mandantes={mandantes}
                    contratos={contratosFor(w.mandante || w.company || "")}
                    onSave={async (mandanteName, contratoName) => {
                      const patch = { mandante: mandanteName };
                      if (contratoName !== undefined) patch.contrato = contratoName;
                      try { await saveFields(patch); } catch (_) {}
                    }} />
                  <InfoRow icon={MapPin} label="Ciudad" value={w.city} />
                  <InfoRow icon={Phone} label="Teléfono" value={w.phone} canEdit={canEdit} kind="phone" onSave={(v) => saveField("phone", v)} />
                  <InfoRow icon={Send} label="WhatsApp" value={w.whatsapp} canEdit={canEdit} kind="whatsapp" onSave={(v) => saveField("whatsapp", v)} />
                  <InfoRow icon={Mail} label="Email" value={w.email} />
                  <InfoRow icon={CalendarClock} label="Fecha ingreso" value={formatDate(w.expected_hire_date)} />
                  <InfoRow icon={CalendarClock} label="Fecha límite" value={formatDate(w.due_date)} />
                  <InfoRow icon={User} label="Responsable" value={w.assigned_user} />
                  <InfoRow icon={Send} label="Últ. contacto" value={`${relativeDays(w.last_contact_at)}${w.last_contact_channel ? " · " + (CHANNEL_LABEL[w.last_contact_channel] || w.last_contact_channel) : ""}`} />
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="font-display font-semibold text-slate-800 mb-3">Requisitos de cumplimiento</h3>
              <div className="space-y-2">
                {(w.requirements || []).map((r) => (
                  <div key={r.code} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-sm text-slate-700">{r.name}</span>
                    <StatusCell testid={`detail-status-${r.code}`} status={r.status} readOnly={!canEdit} onChange={(s) => updateStatus(r.code, s)} />
                  </div>
                ))}
                {(!w.requirements || w.requirements.length === 0) && (
                  <p className="text-xs text-slate-400 italic">Sin requisitos configurados.</p>
                )}
              </div>
            </div>

            {jornadas.length > 0 && (
              <div data-testid="worker-detail-jornadas">
                <h3 className="font-display font-semibold text-slate-800 mb-3">Jornadas excepcionales aplicables</h3>
                <div className="space-y-2">
                  {jornadas.map((j) => {
                    const chip = { SOLICITADA: "bg-slate-100 text-slate-700", EN_TRAMITE: "bg-amber-50 text-amber-700",
                                   AUTORIZADA: "bg-emerald-50 text-emerald-700", RECHAZADA: "bg-red-50 text-red-700" }[j.estado] || "bg-slate-100 text-slate-700";
                    const label = { SOLICITADA: "Solicitada", EN_TRAMITE: "En trámite", AUTORIZADA: "Autorizada", RECHAZADA: "Rechazada" }[j.estado] || j.estado;
                    const fmt = (iso) => { if (!iso) return "—"; const s = String(iso).slice(0,10); const [y,m,d]=s.split("-"); return y&&m&&d?`${d}-${m}-${y}`:s; };
                    const dt = Number(j.dias_trabajo || 0), dd = Number(j.dias_descanso || 0);
                    return (
                      <div key={j.id} className="p-3 rounded-lg bg-indigo-50/40 border border-indigo-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-800">Res. {j.numero_resolucion || "s/n"}</span>
                          <div className="flex items-center gap-1.5">
                            {dt + dd > 0 && (<span className="inline-flex items-center h-6 px-2 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">{dt}×{dd}</span>)}
                            <span className={`inline-flex items-center h-6 px-2 rounded text-xs font-medium ${chip}`}>{label}</span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">Vigencia: {fmt(j.fecha_inicio)} → {fmt(j.fecha_termino)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar {w.first_name} {w.last_name}</AlertDialogTitle>
            <AlertDialogDescription>Esta acción es irreversible. Se eliminarán sus documentos, requisitos, alertas y timeline.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="worker-detail-delete-confirm" disabled={deleting} onClick={deleteWorker} className="bg-red-600 hover:bg-red-700">
              {deleting ? "Eliminando…" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReassignDialog open={reassignOpen} onClose={() => setReassignOpen(false)} worker={w} onDone={load} defaultTab={reassignTab} />
    </div>
  );
}

/* -------- Helpers for Historial table -------- */
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  } catch { return iso; }
}

function buildAuditDetail(r) {
  const m = r.meta || {};
  const parts = [];
  if (m.document_name) parts.push(`Doc: ${m.document_name}`);
  if (m.filename) parts.push(m.filename);
  if (m.expiration_date) parts.push(`Vence ${m.expiration_date}`);
  if (m.status) parts.push(`Estado → ${m.status}`);
  if (m.channel) parts.push(`Canal: ${m.channel}`);
  if (m.action && typeof m.action === "string") parts.push(m.action);
  if (m.from && m.to) parts.push(`${JSON.stringify(m.from)} → ${JSON.stringify(m.to)}`);
  return parts.join(" · ");
}

function buildTimelineDetail(t) {
  if (t.document_name || t.filename || t.expiration_date) {
    const parts = [];
    if (t.document_name) parts.push(`Doc: ${t.document_name}`);
    if (t.filename) parts.push(t.filename);
    if (t.expiration_date) parts.push(`Vence ${t.expiration_date}`);
    return parts.join(" · ");
  }
  return t.text || "";
}

function tlActionLabel(t) {
  const map = {
    import: "Importación",
    created: "Creación",
    reminder: "Recordatorio",
    status_change: "Cambio estado",
    response: "Respuesta",
    manual_contact: "Contacto manual",
    document: t.action === "DELETE" ? "Eliminación documento"
             : t.action === "REPLACE" ? "Reemplazo documento"
             : "Subida documento",
    credencial: "Escaneo credencial",
    reassign: "Reasignación",
    finiquito: "Finiquito",
    traslado: "Traslado",
    unassign: "Desasignación",
  };
  return map[t.type] || t.type || "Evento";
}

function badgeFor(code) {
  if (!code) return "bg-slate-100 text-slate-700";
  if (code.includes("DELETE")) return "bg-red-50 text-red-700";
  if (code.includes("UPLOAD") || code.includes("CREATE")) return "bg-emerald-50 text-emerald-700";
  if (code.includes("REPLACE") || code.includes("UPDATE") || code === "STATUS_CHANGE") return "bg-amber-50 text-amber-700";
  if (code === "REASSIGN") return "bg-indigo-50 text-indigo-700";
  return "bg-slate-100 text-slate-700";
}

/* -------- Datos modal rows (copied from previous Datos tab) -------- */
function InfoRow({ icon: Icon, label, value, canEdit, kind, onSave }) {
  const editable = !!onSave;
  if (!editable) {
    return (
      <tr className="hover:bg-slate-50/50">
        <td className="w-[180px] py-2 px-4 bg-slate-50/60">
          <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            <Icon size={13} className="text-slate-400" /> {label}
          </div>
        </td>
        <td className="py-2 px-4 text-sm text-slate-700">{value || <span className="text-slate-400">—</span>}</td>
      </tr>
    );
  }
  return <EditableRow icon={Icon} label={label} value={value} canEdit={canEdit} kind={kind} onSave={onSave} />;
}

function EditableRow({ icon: Icon, label, value, canEdit, kind, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setVal(value || ""); }, [value, editing]);

  const validate = (v) => {
    if (!v) return "";
    if (kind === "rut") return isValidRut(v) ? "" : "RUT inválido";
    if (kind === "phone") return v === CL_PREFIX ? "" : (isValidClPhone(v) ? "" : "Teléfono inválido");
    if (kind === "whatsapp") return v === WA_PREFIX ? "" : (isValidClWhatsapp(v) ? "" : "WhatsApp inválido (+569 + 8 dígitos)");
    return "";
  };
  const onChange = (e) => {
    const raw = e.target.value;
    if (kind === "rut") setVal(formatRut(raw));
    else if (kind === "phone") setVal(normalizeClPhone(raw));
    else if (kind === "whatsapp") setVal(normalizeClWhatsapp(raw));
    else setVal(raw);
  };
  const err = validate(val);
  const commit = async () => {
    if (err) { toast.error(err); return; }
    let toSend = val;
    if (kind === "rut" && val) toSend = formatRut(val);
    else if (kind === "phone" && val === CL_PREFIX) toSend = "";
    else if (kind === "whatsapp" && val === WA_PREFIX) toSend = "";
    if (toSend === (value || "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(toSend); setEditing(false); }
    catch { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  return (
    <tr className="group hover:bg-slate-50/50">
      <td className="w-[180px] py-2 px-4 bg-slate-50/60">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <Icon size={13} className="text-slate-400" /> {label}
        </div>
      </td>
      <td className="py-2 px-4">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input autoFocus data-testid={`edit-${label.toLowerCase()}-input`} value={val} onChange={onChange}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              className={`flex-1 h-7 px-2 rounded border text-sm outline-none ${err ? "border-red-400" : "border-slate-300 focus:border-blue-400"}`} />
            <button data-testid={`edit-${label.toLowerCase()}-save`} disabled={busy || !!err} onClick={commit} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40"><Check size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
            {err && <p className="text-[10px] text-red-500 ml-1">{err}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-slate-700 truncate">{value || <span className="text-slate-400">—</span>}</span>
            {canEdit && (
              <button data-testid={`edit-${label.toLowerCase()}-btn`}
                onClick={() => { setVal(value || (kind === "phone" ? CL_PREFIX : kind === "whatsapp" ? WA_PREFIX : "")); setEditing(true); }}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition shrink-0" title={`Editar ${label}`}>
                <Pencil size={13} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function AssignmentRow({ canEdit, worker, mandantes, contratos, onSave }) {
  const [editing, setEditing] = useState(false);
  const [mSel, setMSel] = useState(worker.mandante || worker.company || "");
  const [cSel, setCSel] = useState(worker.contrato || "");
  const [busy, setBusy] = useState(false);
  const currentContratos = (mandantes.find((m) => m.name === mSel)?.contratos) || contratos;
  useEffect(() => {
    if (!editing) { setMSel(worker.mandante || worker.company || ""); setCSel(worker.contrato || ""); }
  }, [worker.mandante, worker.company, worker.contrato, editing]);

  const commit = async () => {
    setBusy(true);
    try { await onSave(mSel, cSel); setEditing(false); }
    catch { toast.error("No se pudo guardar"); }
    finally { setBusy(false); }
  };

  const hasAssignment = !!(worker.mandante || worker.company);

  return (
    <tr className="group hover:bg-slate-50/50">
      <td className="w-[180px] py-2 px-4 bg-slate-50/60">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <Building2 size={13} className="text-slate-400" /> Mandante / Contrato
        </div>
      </td>
      <td className="py-2 px-4">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <select data-testid="edit-mandante-select" value={mSel} onChange={(e) => { setMSel(e.target.value); setCSel(""); }}
              className="h-7 px-2 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none min-w-[120px]">
              <option value="">—</option>
              {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
            <select data-testid="edit-contrato-select" value={cSel} onChange={(e) => setCSel(e.target.value)} disabled={!mSel}
              className="h-7 px-2 rounded border border-slate-300 focus:border-blue-400 text-sm bg-white outline-none disabled:opacity-50 min-w-[120px]">
              <option value="">—</option>
              {currentContratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <button data-testid="edit-mandante-save" disabled={busy} onClick={commit} className="text-emerald-600 hover:text-emerald-700"><Check size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            {hasAssignment ? (
              <span className="text-sm text-slate-700 truncate">
                {worker.mandante || worker.company}
                {worker.contrato && <span className="text-slate-400"> · {worker.contrato}</span>}
              </span>
            ) : (
              <span className="text-sm text-slate-400 italic">Sin asignación</span>
            )}
            {canEdit && (
              <button data-testid="edit-mandante-btn" onClick={() => setEditing(true)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition shrink-0" title="Editar mandante y contrato">
                <Pencil size={13} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium transition-colors">
      <Icon size={14} /> {label}
    </button>
  );
}
